import { UnleashClient } from "unleash-proxy-client";
import Vue from "vue";

// Define state keys as object selector
const StateKey = {
  FCS_CONTEXT: "fcscontext",
  // BCS_CONTEXT: "bcscontext",
  FCS_TOGGLES: "fcstoggles",
  // BCS_TOGGLES: "bcstoggles",
};

// Environment configuration constants
const ENV_CONFIG = {
  FCS: {
    URL: process.env.VUE_APP_FCS_OPENFEATURE_URL || "",
    CLIENT_KEY: process.env.VUE_APP_FCS_OPENFEATURE_CLIENT_KEY || "",
    APP_NAME: process.env.VUE_APP_FCS_OPENFEATURE_APP_NAME || "default",
  },
  // BCS: {
  //   URL: process.env.VUE_APP_BCS_OPENFEATURE_URL || "",
  //   CLIENT_KEY: process.env.VUE_APP_BCS_OPENFEATURE_CLIENT_KEY || "",
  //   APP_NAME: process.env.VUE_APP_BCS_OPENFEATURE_APP_NAME || "default",
  // },
};

// String constants to avoid primitive obsession
const STRING_CONSTANTS = {
  UPDATE_EVENT: "update",
  EMPTY_STRING: "",
  NAMESPACE_SEPARATOR: ".",
  VUE_PLUGIN_PROPERTIES: {
    UNLEASH_STATE: "$unleashState",
    FCS_FLAG_CLIENT: "$fcsFlagClient",
    // BCS_FLAG_CLIENT: "$bcsFlagClient",
    FCS_FLAG: "$fcsFlag",
    // BCS_FLAG: "$bcsFlag",
  },
};

// Create a reactive state store to trigger rerenders
export const unleashState = Vue.observable({
  [StateKey.FCS_CONTEXT]: "",
  // [StateKey.BCS_CONTEXT]: "",
  [StateKey.FCS_TOGGLES]: "",
  // [StateKey.BCS_TOGGLES]: "",
});

// Client configurations
const clientConfigs = [
  {
    name: "fcs",
    url: ENV_CONFIG.FCS.URL,
    clientKey: ENV_CONFIG.FCS.CLIENT_KEY,
    appName: ENV_CONFIG.FCS.APP_NAME,
    contextKey: StateKey.FCS_CONTEXT,
    togglesKey: StateKey.FCS_TOGGLES,
  },
  // {
  //   name: "bcs",
  //   url: ENV_CONFIG.BCS.URL,
  //   clientKey: ENV_CONFIG.BCS.CLIENT_KEY,
  //   appName: ENV_CONFIG.BCS.APP_NAME,
  //   contextKey: StateKey.BCS_CONTEXT,
  //   togglesKey: StateKey.BCS_TOGGLES,
  // },
];

// Create clients dynamically
const clients = new Map();
clientConfigs.forEach((config) => {
  try {
    clients.set(
      config.name,
      new UnleashClient({
        url: config.url,
        clientKey: config.clientKey,
        appName: config.appName,
      })
    );
  } catch (error) {
    console.error(`Failed to create Unleash client ${config.name}:`, error);
    return;
  }
});

// Export clients for backward compatibility
export const fcsFlagClient = clients.get("fcs");
// export const bcsFlagClient = clients.get("bcs");

const triggerReactive = (client, config) => {
  const originalIsEnabled = client.isEnabled;
  const originalGetVariant = client.getVariant;
  // Override methods to make them reactive
  client.isEnabled = function(name) {
    // Access the reactive properties to create dependencies
    void unleashState[config.contextKey];
    void unleashState[config.togglesKey];

    const result = originalIsEnabled.call(this, name);

    return result;
  };

  client.getVariant = function(name) {
    // Access the reactive properties to create dependencies
    void unleashState[config.contextKey];
    void unleashState[config.togglesKey];

    const result = originalGetVariant.call(this, name);

    return result;
  };
}

// Helper function to make client methods reactive
function makeClientReactive(client, config) {

  // Set up listener for updates
  client.on(STRING_CONSTANTS.UPDATE_EVENT, () => {
    unleashState[config.contextKey] = JSON.stringify(client.getContext());
    unleashState[config.togglesKey] = JSON.stringify(client.getAllToggles());
  });

  triggerReactive(client, config);
}

let isReady = false;

export const waitForUnleashReadiness = (isProfileError = false) => {
  return new Promise((resolve) => {
    const token = process.env.VUE_APP_TOKEN_URL
    
    if (!token || isReady || isProfileError) {
      resolve();
      return;
    }
    Promise.allSettled(clientConfigs.map((config) => {
      const client = clients.get(config.name);
      if (!client) {
        return Promise.resolve();
      }
      return ((res) => {
        let timeoutInstance = null;
        const checkContext = async () => {
          const context = client.getContext();
          if (context?.email) {
            isReady = true;
            client.off(STRING_CONSTANTS.UPDATE_EVENT, checkContext);
            clearTimeout(timeoutInstance);
            await client.updateToggles();
            res();
          }
        }
        client.on(STRING_CONSTANTS.UPDATE_EVENT, checkContext);
        timeoutInstance = setTimeout(() => {
          console.log(`Unleash client ${config.name} readiness check timed out.`);
          client.off(STRING_CONSTANTS.UPDATE_EVENT, checkContext);
          res();
        }, 10000);
      });
    })).then(() => {
      resolve();
    });
  });
};

// Initialize both clients
export const initUnleashClients = async () => {
  try {
    await Promise.allSettled(
      clientConfigs.map((config) => {
        const client = clients.get(config.name);
        if (!client) {
          console.error(`[Unleash Debug] Client ${config.name} not found!`);
          return Promise.resolve();
        }
        makeClientReactive(client, config);
        return client.start();
      })
    );
  } catch (error) {
    console.error("Failed to initialize Unleash clients:", error);
    return;
  }
};

// Custom context object for targeting rules
export const updateUnleashContext = (context) => {
  try {
    clients.forEach((client) => {
      client.updateContext(context);
    });
  } catch (error) {
    console.error("Failed to update Unleash context:", error);
    return;
  }
};

export const updateContextField = (field, value) => {
  try {
    clients.forEach((client) => {
      client.setContextField(field, value);
    });
  } catch (error) {
    console.error("Failed to update Unleash context:", error);
    return;
  }
};

// Base flag classes
class BaseFlag {
  constructor(defaultValue = false, client, flagName) {
    this.defaultValue = defaultValue;
    this.client = client;
    this._name = flagName || "";
  }

  get name() {
    return this._name;
  }

  setName(name) {
    if (!this._name) {
      this._name = name;
    }
  }

  unfreeze() {
    // No-op for compatibility with Rox
  }
}

class Flag extends BaseFlag {
  constructor(
    defaultValue = false,
    client,
    flagName
  ) {
    super(defaultValue, client, flagName);
  }

  isEnabled() {
    try {
      const variant = this.client.getVariant(this._name);
      const payloadValue = variant.payload?.value;

      if (payloadValue !== undefined && payloadValue !== null) {
        const normalized = String(payloadValue).trim().toLowerCase();
        if (normalized === "true") return true;
        if (normalized === "false") return false;

        const parsedValue = Number(normalized);
        if (!isNaN(parsedValue)) {
          return !!parsedValue;
        }
      }

      return this.client.isEnabled(this._name) ?? this.defaultValue;
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

class StringFlag extends BaseFlag {
  constructor(defaultValue = "", client, flagName) {
    super(defaultValue, client, flagName);
  }

  getValue() {
    try {
      const variant = this.client.getVariant(this._name);
      return variant.payload?.value ?? this.defaultValue;
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

class JSONFlag extends BaseFlag {
  constructor(defaultValue = {}, client, flagName) {
    super(defaultValue, client, flagName);
  }

  getValue() {
    try {
      const variant = this.client.getVariant(this._name);
      return JSON.parse(variant.payload?.value || JSON.stringify(this.defaultValue));
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

class NumberFlag extends BaseFlag {
  constructor(defaultValue = 0, client, flagName) {
    super(defaultValue, client, flagName);
  }

  getValue() {
    try {
      const variant = this.client.getVariant(this._name);
      const variantValue = variant.payload?.value;
      if (variantValue === undefined) return this.defaultValue;
      const numValue = Number(variantValue);
      return isNaN(numValue) ? this.defaultValue : numValue;
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

// Factory function to create flag namespaces
function createFlagNamespace(client) {
  const registeredFlags = {};

  const isValidFlag = (flag) => {
    return (
      flag !== null &&
      flag !== undefined &&
      typeof flag === "object" &&
      "setName" in flag
    );
  };

  const shouldSetName = (
    flag
  ) => {
    return (
      flag.name === STRING_CONSTANTS.EMPTY_STRING &&
      typeof flag.setName === "function"
    );
  };

  const buildFlagName = (namespace, key) => {
    return namespace
      ? `${namespace}${STRING_CONSTANTS.NAMESPACE_SEPARATOR}${key}`
      : key;
  };

  return {
    register(namespace, flagContainer) {
      registeredFlags[namespace] = flagContainer;

      // Iterate through flags to set their names based on the keys
      for (const key in flagContainer) {
        const flag = flagContainer[key];

        if (isValidFlag(flag) && shouldSetName(flag, namespace, key)) {
          const flagName = buildFlagName(namespace, key);
          flag.setName(flagName);
        }
      }
    },

    Flag: class extends Flag {
      constructor(defaultValue = false, flagName) {
        super(defaultValue, client, flagName);
      }
    },

    StringFlag: class extends StringFlag {
      constructor(defaultValue = "", flagName) {
        super(defaultValue, client, flagName);
      }
    },

    NumberFlag: class extends NumberFlag {
      constructor(defaultValue = 0, flagName) {
        super(defaultValue, client, flagName);
      }
    },

    JSONFlag: class extends JSONFlag {
      constructor(defaultValue = {}, flagName) {
        super(defaultValue, client, flagName);
      }
    },

    isEnabled(flagName, defaultValue = false) {
      const result = client.isEnabled(flagName) ?? defaultValue;
      return result;
    },

    getVariant(flagName, defaultValue = "") {
      const result = client.getVariant(flagName)?.name ?? defaultValue;
      return result;
    },
  };
}

// Create flag namespaces using the factory
export const FcsFlag = createFlagNamespace(fcsFlagClient);
// export const BcsFlag = createFlagNamespace(bcsFlagClient);

export const createUnleashPlugin = () => {
  return {
    install(Vue) {
      // Make the reactive state accessible
      Vue.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.UNLEASH_STATE
      ] = unleashState;

      // Provide client access
      Vue.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.FCS_FLAG_CLIENT
      ] = fcsFlagClient;
      // Vue.prototype[
      //   STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.BCS_FLAG_CLIENT
      // ] = bcsFlagClient;

      // Provide flag namespace access
      Vue.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.FCS_FLAG
      ] = FcsFlag;
      // Vue.prototype[
      //   STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.BCS_FLAG
      // ] = BcsFlag;
    },
  };
};
